import { useMemo } from "react"
import type { Query as TCubeQuery } from "@cubejs-client/core"

import type { Option as SearchOption } from "~/ui-rtk/components/ui/common/SearchSelect"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import useUrlSorting from "~/ui-rtk/hooks/url-sorting"
import { formatNumber } from "~/ui-rtk/utils/format"

import {
  CHART_ID,
  OPTIONS_LIMIT,
  ROW_LIMIT,
  SUBSEQUENT_CUBES,
  type SubsequentGrain,
} from "./constants"
import {
  DEFAULT_SORTING,
  EMPTY_DENOMINATORS,
  GAP_BUCKET_DAYS,
  buildRows,
  sortRows,
  type Denominators,
  type RawPair,
  type SubsequentRow,
} from "./utils/subsequent-rows"

export type SubsequentFilters = {
  grain: SubsequentGrain
  skuPrefixLength: number
  /** Selected original product (product grain). */
  productId: string | null
  /** Selected original SKU prefix at skuPrefixLength (SKU grain). */
  skuPrefix: string | null
  /** Sequence of the original order; empty = any of 1-4. */
  orderSequences: string[]
}

type CubeFilter = {
  member: string
  operator: "equals"
  values: string[]
}

type RawRow = Record<string, unknown>

const num = (value: unknown) => Number(value ?? 0) || 0

/** The original item the analysis is keyed on, or null until one is picked. */
export const selectedItemKey = (filters: SubsequentFilters): string | null =>
  filters.grain === "product" ? filters.productId : filters.skuPrefix

/**
 * Filters shared by every query on the selected grain: company, and for the
 * SKU table the mandatory prefix level (rows are duplicated across levels).
 */
function useGrainFilters(
  cube: string,
  grain: SubsequentGrain,
  companyId: string | undefined,
  skuPrefixLength: number,
) {
  return useMemo<CubeFilter[]>(
    () => [
      {
        member: `${cube}.companyid`,
        operator: "equals",
        values: [companyId ?? ""],
      },
      ...(grain === "sku"
        ? [
            {
              member: `${cube}.prefix_length`,
              operator: "equals" as const,
              values: [String(skuPrefixLength)],
            },
          ]
        : []),
    ],
    [cube, grain, companyId, skuPrefixLength],
  )
}

/**
 * Picker options: the original items with the most orders in the window,
 * read from BASE rows so each order counts once. Intentionally not narrowed
 * by the order-sequence filter so the list stays stable while filtering.
 */
function useItemOptions(
  cube: string,
  grain: SubsequentGrain,
  companyId: string | undefined,
  range: { start: string; end: string },
  grainFilters: CubeFilter[],
) {
  const keyDimension =
    grain === "product" ? `${cube}.product_id` : `${cube}.sku_prefix`
  const labelDimension =
    grain === "product" ? `${cube}.product_title` : keyDimension

  const query = useMemo<TCubeQuery>(
    () => ({
      dimensions: Array.from(new Set([keyDimension, labelDimension])),
      measures: [`${cube}.original_orders`],
      timeDimensions: [
        {
          dimension: `${cube}.order_date`,
          dateRange: [range.start, range.end],
        },
      ],
      filters: [
        ...grainFilters,
        {
          member: `${cube}.row_type`,
          operator: "equals",
          values: ["base"],
        },
      ],
      order: { [`${cube}.original_orders`]: "desc" },
      limit: OPTIONS_LIMIT,
    }),
    [cube, keyDimension, labelDimension, range.start, range.end, grainFilters],
  )

  const { resultSet, isLoading } = useCubeQuery(query, { skip: !companyId })

  const options = useMemo<SearchOption[]>(() => {
    const rows = (resultSet?.rawData() ?? []) as RawRow[]
    const seen = new Map<string, SearchOption>()
    for (const row of rows) {
      const value = String(row[keyDimension] ?? "")
      if (!value || seen.has(value)) continue
      const title = String(row[labelDimension] || value)
      const orders = num(row[`${cube}.original_orders`])
      seen.set(value, {
        value,
        label: `${title} · ${formatNumber(orders, { decimals: 0 })} orders`,
      })
    }
    return Array.from(seen.values())
  }, [resultSet, cube, keyDimension, labelDimension])

  return { options, isLoading: companyId ? isLoading : false }
}

export default function useConnect({
  filters,
  range,
}: {
  filters: SubsequentFilters
  range: { start: string; end: string }
}) {
  const currentCompany = useCurrentCompany()
  const companyId = currentCompany?.id

  const { grain } = filters
  const cube = SUBSEQUENT_CUBES[grain]
  const itemKey = selectedItemKey(filters)

  const grainFilters = useGrainFilters(
    cube,
    grain,
    companyId,
    filters.skuPrefixLength,
  )

  const itemOptions = useItemOptions(
    cube,
    grain,
    companyId,
    range,
    grainFilters,
  )

  const selectionFilters = useMemo<CubeFilter[]>(
    () => [
      ...grainFilters,
      {
        member:
          grain === "product" ? `${cube}.product_id` : `${cube}.sku_prefix`,
        operator: "equals",
        values: [itemKey ?? ""],
      },
      ...(filters.orderSequences.length > 0
        ? [
            {
              member: `${cube}.order_sequence`,
              operator: "equals" as const,
              values: filters.orderSequences,
            },
          ]
        : []),
    ],
    [grainFilters, grain, cube, itemKey, filters.orderSequences],
  )

  const timeDimensions = useMemo(
    () => [
      {
        dimension: `${cube}.order_date`,
        dateRange: [range.start, range.end] as [string, string],
      },
    ],
    [cube, range.start, range.end],
  )

  const subsequentDimensions = useMemo(
    () =>
      grain === "product"
        ? [
            `${cube}.subsequent_product_id`,
            `${cube}.subsequent_product_title`,
            `${cube}.subsequent_product_handle`,
          ]
        : [`${cube}.subsequent_sku_prefix`],
    [grain, cube],
  )

  // PAIR rows: one per item found in the customers' next orders.
  const pairQuery = useMemo<TCubeQuery>(
    () => ({
      dimensions: subsequentDimensions,
      measures: [
        `${cube}.subsequent_orders`,
        `${cube}.subsequent_quantity`,
        `${cube}.subsequent_revenue`,
      ],
      timeDimensions,
      filters: [
        ...selectionFilters,
        {
          member: `${cube}.row_type`,
          operator: "equals",
          values: ["pair"],
        },
      ],
      order: { [`${cube}.subsequent_orders`]: "desc" },
      limit: ROW_LIMIT,
    }),
    [cube, subsequentDimensions, timeDimensions, selectionFilters],
  )

  // BASE rows: the denominators for the selected item, summed over the
  // window and sequences. Base rows are unique per (item, sequence, date), so
  // the sum never double counts across next-order items.
  const baseQuery = useMemo<TCubeQuery>(
    () => ({
      measures: [
        `${cube}.original_orders`,
        `${cube}.original_orders_with_next`,
      ],
      timeDimensions,
      filters: [
        ...selectionFilters,
        {
          member: `${cube}.row_type`,
          operator: "equals",
          values: ["base"],
        },
      ],
    }),
    [cube, timeDimensions, selectionFilters],
  )

  // Order-gap timing (summed days to the next order and the cumulative
  // within-N-days counts) lives in columns added after the report shipped, so
  // it is read in its own queries and treated as optional: a company whose
  // snapshot predates the columns gets "--" for the gap figures instead of a
  // broken page.
  const timingBaseQuery = useMemo<TCubeQuery>(
    () => ({
      measures: [
        `${cube}.sum_days_to_next`,
        ...GAP_BUCKET_DAYS.map(days => `${cube}.next_within_${days}_days`),
      ],
      timeDimensions,
      filters: [
        ...selectionFilters,
        {
          member: `${cube}.row_type`,
          operator: "equals",
          values: ["base"],
        },
      ],
    }),
    [cube, timeDimensions, selectionFilters],
  )

  const subsequentKeyDimension =
    grain === "product"
      ? `${cube}.subsequent_product_id`
      : `${cube}.subsequent_sku_prefix`

  const timingPairQuery = useMemo<TCubeQuery>(
    () => ({
      dimensions: [subsequentKeyDimension],
      measures: [`${cube}.subsequent_sum_days_to_next`],
      timeDimensions,
      filters: [
        ...selectionFilters,
        {
          member: `${cube}.row_type`,
          operator: "equals",
          values: ["pair"],
        },
      ],
      limit: ROW_LIMIT,
    }),
    [cube, subsequentKeyDimension, timeDimensions, selectionFilters],
  )

  const skip = !companyId || !itemKey
  const {
    resultSet: pairResultSet,
    isLoading: isPairsLoading,
    error: pairsError,
  } = useCubeQuery(pairQuery, { skip })
  const {
    resultSet: baseResultSet,
    isLoading: isBaseLoading,
    error: baseError,
  } = useCubeQuery(baseQuery, { skip })
  const {
    resultSet: timingBaseResultSet,
    isLoading: isTimingBaseLoading,
    error: timingBaseError,
  } = useCubeQuery(timingBaseQuery, { skip })
  const {
    resultSet: timingPairResultSet,
    isLoading: isTimingPairLoading,
    error: timingPairError,
  } = useCubeQuery(timingPairQuery, { skip })

  const denominators = useMemo<Denominators>(() => {
    const row = ((baseResultSet?.rawData() ?? []) as RawRow[])[0]
    if (!row) return EMPTY_DENOMINATORS
    const timingRow = timingBaseError
      ? undefined
      : ((timingBaseResultSet?.rawData() ?? []) as RawRow[])[0]
    return {
      originalOrders: num(row[`${cube}.original_orders`]),
      ordersWithNext: num(row[`${cube}.original_orders_with_next`]),
      timing: timingRow
        ? {
            sumDaysToNext: num(timingRow[`${cube}.sum_days_to_next`]),
            nextWithin: {
              30: num(timingRow[`${cube}.next_within_30_days`]),
              60: num(timingRow[`${cube}.next_within_60_days`]),
              90: num(timingRow[`${cube}.next_within_90_days`]),
              180: num(timingRow[`${cube}.next_within_180_days`]),
              365: num(timingRow[`${cube}.next_within_365_days`]),
            },
          }
        : null,
    }
  }, [baseResultSet, timingBaseResultSet, timingBaseError, cube])

  // Summed days to the next order per next-order item; null when unavailable.
  const pairTiming = useMemo<Map<string, number> | null>(() => {
    if (timingPairError || !timingPairResultSet) return null
    const byKey = new Map<string, number>()
    for (const row of timingPairResultSet.rawData() as RawRow[]) {
      const key = String(row[subsequentKeyDimension] ?? "")
      if (key) {
        byKey.set(key, num(row[`${cube}.subsequent_sum_days_to_next`]))
      }
    }
    return byKey
  }, [timingPairResultSet, timingPairError, subsequentKeyDimension, cube])

  const { sorting } = useUrlSorting(CHART_ID, DEFAULT_SORTING)

  const rows = useMemo<SubsequentRow[]>(() => {
    if (!itemKey) return []
    const raw = (pairResultSet?.rawData() ?? []) as RawRow[]
    const pairs: RawPair[] = raw
      .map(row => {
        const key =
          grain === "product"
            ? String(row[`${cube}.subsequent_product_id`] ?? "")
            : String(row[`${cube}.subsequent_sku_prefix`] ?? "")
        const title =
          grain === "product"
            ? String(row[`${cube}.subsequent_product_title`] || key)
            : key
        const handle =
          grain === "product"
            ? String(row[`${cube}.subsequent_product_handle`] ?? "") || null
            : null
        return {
          itemKey: key,
          title,
          handle,
          subsequentOrders: num(row[`${cube}.subsequent_orders`]),
          subsequentQuantity: num(row[`${cube}.subsequent_quantity`]),
          subsequentRevenue: num(row[`${cube}.subsequent_revenue`]),
          sumDaysToNext: pairTiming?.get(key) ?? null,
        }
      })
      .filter(pair => pair.itemKey !== "")
    return sortRows(buildRows(pairs, denominators), sorting)
  }, [itemKey, pairResultSet, grain, cube, denominators, pairTiming, sorting])

  return {
    rows,
    denominators,
    itemOptions,
    isLoading:
      !skip &&
      (isPairsLoading ||
        isBaseLoading ||
        isTimingBaseLoading ||
        isTimingPairLoading),
    // Timing errors are deliberately not surfaced: the gap figures fall back
    // to "--" while the rest of the report still renders.
    error: pairsError ?? baseError ?? null,
    hasSelection: Boolean(itemKey),
  }
}
